import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowDownTrayIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { TFunction } from 'i18next';

import { GetMessageResponse, useMessage } from 'services/repositories/messages/messages';
import { downloadReceiverEnvelope, useRecoverTransaction } from 'services/repositories/transactions/transactions';
import { useHistoryStore } from 'stores/history/history';
import { DEFAULT_REFETCH_INTERVAL, EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { DocumentType, TransactionState } from 'support/types';
import { useConnectionNavigation } from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { Section } from 'components/Display/Section/Section';
import {
  SlideOver,
  SlideOverContentSkeleton,
  SlideOverSubtitleSkeleton,
  SlideOverTitleSkeleton,
} from 'components/Display/SlideOver/SlideOver';
import { Divider } from 'components/Divider/Divider';
import { Button } from 'components/Form/Button/Button';
import { PartnerLogo } from 'components/PartnerLogo/PartnerLogo';
import { TestingFlowMessageStateDisplay } from 'components/TestingFlowMessageStateDisplay/TestingFlowMessageStateDisplay';
import { TransactionBlockingError } from 'components/TransactionBlockingError/TransactionBlockingError';

type TestMessageDetailsProps = {
  open: boolean;
};

export const TestMessageDetails: FC<TestMessageDetailsProps> = ({ open }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { history } = useHistoryStore();
  const { partnerId, relationshipId, messageId } = useParams<{
    partnerId: string;
    relationshipId: string;
    messageId: string;
  }>();
  const { routeTo } = useConnectionNavigation();
  const { data: message, isLoading: isLoadingMessage } = useMessage({
    variables: { messageId },
    enabled: Boolean(messageId),
    keepPreviousData: !open,
    refetchInterval: DEFAULT_REFETCH_INTERVAL,
    staleTime: 0,
  });

  const { mutate, isLoading: isRecoveringMessage } = useRecoverTransaction();

  const onBack = () => {
    if (partnerId && relationshipId) {
      navigate(routeTo('testRelationship', { partnerId, relationshipId }));
    }
  };
  const onClose = () =>
    navigate(routeTo('relationshipDetails', { partnerId: partnerId!, relationshipId: relationshipId! }));

  const messageData = message?.data;

  return (
    <SlideOver
      size="large"
      open={open}
      onBack={history.length <= 1 ? undefined : onBack}
      setOpen={onClose}
      title={isLoadingMessage ? <SlideOverTitleSkeleton /> : messageData?.sender_partner?.name}
      subtitle={
        isLoadingMessage ? (
          <SlideOverSubtitleSkeleton />
        ) : (
          `${t('connections:connectionsTest.drawer.subtitle', {
            documentType: t(`common:messageTypes.plural.${messageData?.type}`),
          })} - ${messageData?.external_message_ref}`
        )
      }
      headerImage={
        <PartnerLogo imageUrl={messageData?.sender_partner?.logoUrl} size="large" isLoading={isLoadingMessage} />
      }
    >
      <TestMessageContent
        isLoading={isLoadingMessage}
        messageData={messageData}
        isRecovering={isRecoveringMessage}
        onRecover={() => messageId && mutate({ id: messageId })}
        t={t}
      />
    </SlideOver>
  );
};

type TestMessageData = GetMessageResponse['data'];

type TestMessageContentProps = {
  isLoading: boolean;
  messageData: TestMessageData | undefined;
  isRecovering: boolean;
  onRecover: () => void;
  t: TFunction;
};

const TestMessageContent = ({ isLoading, messageData, isRecovering, onRecover, t }: TestMessageContentProps) => {
  if (isLoading || !messageData) {
    return <SlideOverContentSkeleton />;
  }

  const receiverEnvelope = messageData.receiver_envelope;

  return (
    <div className="space-y-6">
      {messageData.state === TransactionState.failed ? (
        <FailedTestMessageContent messageData={messageData} isRecovering={isRecovering} onRecover={onRecover} t={t} />
      ) : null}
      <div className="space-y-3">
        <Section.Item direction="horizontal" title={t('connections:testMessageDetails.documentId')}>
          {messageData.external_message_ref}
        </Section.Item>
        {messageData.type !== DocumentType.order ? (
          <Section.Item direction="horizontal" title={t('connections:testMessageDetails.orderNumber')}>
            {messageData.related_order?.external_message_ref || EMPTY_FIELD}
          </Section.Item>
        ) : null}
        <Section.Item direction="horizontal" title={t('connections:testMessageDetails.documentStatus')}>
          <div className="flex gap-3">
            <TestingFlowMessageStateDisplay state={receiverEnvelope?.state || messageData.internal_state} receiver />
            {receiverEnvelope?.externalDocumentUrl ? (
              <Link to={receiverEnvelope.externalDocumentUrl} target="_blank">
                <Button
                  size="extra-small"
                  variant="secondary"
                  RightIcon={ArrowTopRightOnSquareIcon}
                  analyticsId="testing-flow:document-in-external-system-navigate"
                >
                  {t('connections:testMessageDetails.viewDocument', {
                    connector: t(`integrations:connector.${messageData.receiver_integration?.connector}.name`),
                  })}
                </Button>
              </Link>
            ) : null}
          </div>
        </Section.Item>
        <Section.Item direction="horizontal" title={t('connections:testMessageDetails.deliveryTime')}>
          {receiverEnvelope?.deliveredAt ? formatDayAndTime(receiverEnvelope.deliveredAt) : EMPTY_FIELD}
        </Section.Item>
        <Section.Item direction="horizontal" title={t('connections:testMessageDetails.integrationName')}>
          {messageData.receiver_integration?.name || EMPTY_FIELD}
        </Section.Item>
        {receiverEnvelope?.fileName ? (
          <Section.Item direction="horizontal" title={t('connections:testMessageDetails.fileName')}>
            <span>{receiverEnvelope.fileName}</span>
            <Button
              size="extra-small"
              variant="secondary"
              iconOnly
              LeftIcon={ArrowDownTrayIcon}
              onClick={() => receiverEnvelope && downloadReceiverEnvelope(receiverEnvelope.id)}
              analyticsId="testing-flow:document-download"
            />
          </Section.Item>
        ) : null}
        <Section.Item direction="horizontal" title={t('connections:testMessageDetails.receiverTechnicalId')}>
          {receiverEnvelope?.technicalReferenceId || EMPTY_FIELD}
        </Section.Item>
      </div>
    </div>
  );
};

const FailedTestMessageContent = ({
  messageData,
  isRecovering,
  onRecover,
  t,
}: Omit<TestMessageContentProps, 'isLoading'> & { messageData: TestMessageData }) => (
  <>
    <div className="flex w-full items-end justify-between">
      <div className="space-y-2">
        <Section.Title variant="small">{t('connections:testMessageDetails.failedStatus.title')}</Section.Title>
        <Section.Subtitle>{t('connections:testMessageDetails.failedStatus.subtitle')}</Section.Subtitle>
      </div>
      {messageData.customer_can_retry ? (
        <Button
          variant="secondary"
          size="small"
          LeftIcon={ArrowPathIcon}
          loading={isRecovering}
          onClick={onRecover}
          analyticsId="testing-flow:message-recover"
        >
          {t('connections:testMessageDetails.failedStatus.action')}
        </Button>
      ) : null}
    </div>
    <TransactionBlockingError transaction={messageData} />
    <Divider />
  </>
);
